import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import TopBar from "../components/risikoanalyseStep/TopBar";
import { RisikoKfzStepForm } from "./RisikoKfzStepForm";
import { RisikoImmobilieStepForm } from "./RisikoImmobilieStepForm";
import { RisikoKennzahlenStepForm } from "./RisikoKennzahlenStepForm";

type TriggeredEntry = { key: string; title: string; bereich: 1 | 2 | 3 | 4 };

type DetailKind = "kfz" | "immobilie" | "kennzahlen" | "info-only";

type DetailState = {
  kind: DetailKind;
  status: "not_started" | "in_progress" | "done";
  answers?: unknown;
};

const DRAFT_LS_PREFIX = "risikoanalyse-detail-draft:";

const BEREICH_TITLES: Record<1 | 2 | 3 | 4, string> = {
  1: "Bereich 1 — Sachwerte",
  2: "Bereich 2 — Haftung / Recht",
  3: "Bereich 3 — Menschen im Unternehmen",
  4: "Bereich 4 — Liquidität / Ertragsausfall",
};

const useStyles = makeStyles({
  pageState: {
    minHeight: ["100vh", "100dvh"],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
  },
  pageStateCard: {
    maxWidth: "480px",
    paddingTop: "32px",
    paddingBottom: "32px",
    paddingLeft: "24px",
    paddingRight: "24px",
    backgroundColor: "rgba(255,255,255,0.05)",
    textAlign: "center",
    ...shorthands.borderRadius("8px"),
  },
  pageStateTitle: {
    margin: 0,
    marginBottom: "8px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.8rem",
    color: BRAND.gold,
  },
  pageStateBody: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: "rgba(255,255,255,0.8)",
  },
  shell: {
    minHeight: ["100vh", "100dvh"],
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
  },
  inner: {
    width: "100%",
    maxWidth: "1080px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "24px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: "60px",
    "@media (min-width: 768px)": {
      paddingLeft: "32px",
      paddingRight: "32px",
    },
  },
  hero: {
    marginTop: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    color: BRAND.ink,
    fontWeight: 400,
    marginBottom: "8px",
  },
  description: {
    marginTop: 0,
    color: BRAND.muted,
    fontSize: "1rem",
    marginBottom: "24px",
  },
  bereich: {
    marginBottom: "20px",
  },
  bereichHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    color: BRAND.white,
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    marginBottom: "10px",
    ...shorthands.borderRadius("8px"),
  },
  bereichBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontWeight: 700,
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  card: {
    backgroundColor: BRAND.white,
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "18px",
    paddingRight: "18px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
  },
  cardBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    color: BRAND.white,
    fontWeight: 700,
    fontSize: "0.85rem",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
  },
  cardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    color: BRAND.ink,
    flexGrow: 1,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    paddingTop: "3px",
    paddingBottom: "3px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    ...shorthands.borderRadius("999px"),
  },
  statusNotStarted: {
    backgroundColor: BRAND.bg,
    color: BRAND.muted,
  },
  statusInProgress: {
    backgroundColor: "rgba(247,168,35,0.18)",
    color: "#7a4c00",
  },
  statusDone: {
    backgroundColor: "rgba(46,125,79,0.16)",
    color: BRAND.pillarGreen,
  },
  cardBody: {
    fontSize: "0.88rem",
    color: BRAND.muted,
  },
  progressBar: {
    height: "4px",
    width: "100%",
    backgroundColor: BRAND.borderDashed,
    ...shorthands.borderRadius("999px"),
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    backgroundColor: BRAND.purple,
    transitionProperty: "width",
    transitionDuration: "300ms",
  },
  cardActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  primaryBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.purple,
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    fontWeight: 700,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("6px"),
    ":hover": {
      backgroundColor: BRAND.ink,
      ...shorthands.borderColor(BRAND.ink),
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },
  doneBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.pillarGreen,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    fontWeight: 700,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.pillarGreen),
    ...shorthands.borderRadius("6px"),
  },
  finalBar: {
    marginTop: "32px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, #3a1c52 100%)`,
    ...shorthands.borderRadius("10px"),
    color: BRAND.white,
  },
  finalLeft: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  finalTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    margin: 0,
  },
  finalSub: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.85)",
  },
  submitBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.gold,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 700,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    alignSelf: "flex-start",
    ...shorthands.border("1px", "solid", BRAND.gold),
    ...shorthands.borderRadius("6px"),
    ":hover": {
      backgroundColor: "#e89d20",
      ...shorthands.borderColor("#e89d20"),
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
  },
  detailWrap: {
    backgroundColor: BRAND.white,
    paddingTop: "24px",
    paddingBottom: "32px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },
  backLink: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    fontWeight: 600,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "0",
    paddingRight: "0",
    ...shorthands.border("0"),
    marginBottom: "16px",
    ":hover": {
      textDecorationLine: "underline",
    },
  },
});

const detailKindFor = (key: string): DetailKind => {
  const k = key.toLowerCase();
  if (k.includes("kfz")) return "kfz";
  if (k.includes("immo") || k === "sachwerte") return "immobilie";
  if (k.includes("kennzahlen") || k.includes("liquid") || k.includes("umsatz")) return "kennzahlen";
  return "info-only";
};

const computeProgress = (state: DetailState): number => {
  if (state.status === "done") return 1;
  if (state.status === "in_progress") return 0.5;
  return 0;
};

const RisikoanalyseDetailFormPublic = () => {
  const styles = useStyles();
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);
  const [companyName, setCompanyName] = useState("");

  const [triggered, setTriggered] = useState<TriggeredEntry[]>([]);
  const [statesByKey, setStatesByKey] = useState<Record<string, DetailState>>({});
  const [activeKey, setActiveKey] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setValid(false);
      setLoading(false);
      return;
    }
    const run = async () => {
      try {
        const res = await fetch(apiUrl(`public-form/${token}`));
        if (!res.ok) {
          setValid(false);
          return;
        }
        setValid(true);
        const data = await res.json();
        const c = data?.contact;
        if (c) setCompanyName(c.displayName || c.lastname || "");

        // Token submittedData was pre-seeded by the link generator with `triggered`.
        const dataRes = await fetch(apiUrl(`public-form/${token}/data`));
        if (dataRes.ok) {
          const td = await dataRes.json();
          const arr = (td?.submittedData?.triggered as TriggeredEntry[] | undefined) ?? [];
          setTriggered(arr);
          const fresh: Record<string, DetailState> = {};
          for (const t of arr) {
            fresh[t.key] = { kind: detailKindFor(t.key), status: "not_started" };
          }
          setStatesByKey(fresh);
        }
        // Restore draft
        try {
          const raw = window.localStorage.getItem(DRAFT_LS_PREFIX + token);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.statesByKey) setStatesByKey(parsed.statesByKey);
          }
        } catch {
          /* ignore */
        }
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token]);

  // Auto-save the per-detail state.
  useEffect(() => {
    if (!token) return;
    try {
      window.localStorage.setItem(
        DRAFT_LS_PREFIX + token,
        JSON.stringify({ statesByKey, savedAt: new Date().toISOString() })
      );
    } catch {
      /* ignore */
    }
  }, [token, statesByKey]);

  const grouped = useMemo(() => {
    const out: Record<1 | 2 | 3 | 4, TriggeredEntry[]> = { 1: [], 2: [], 3: [], 4: [] };
    for (const t of triggered) out[t.bereich].push(t);
    return out;
  }, [triggered]);

  const allDone = useMemo(
    () => triggered.length > 0 && triggered.every((t) => statesByKey[t.key]?.status === "done"),
    [triggered, statesByKey]
  );

  const setDone = (key: string, answers: unknown) => {
    setStatesByKey((prev) => ({
      ...prev,
      [key]: { kind: prev[key]?.kind ?? detailKindFor(key), status: "done", answers },
    }));
    setActiveKey(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmitAll = async () => {
    if (!token) return;
    setSubmitting(true);
    try {
      // Pull KFZ / Immobilie / Kennzahlen out of the per-key answer slots
      const pickByKind = (kind: DetailKind) => {
        for (const t of triggered) {
          const s = statesByKey[t.key];
          if (s?.kind === kind && s.status === "done") return s.answers;
        }
        return undefined;
      };
      const details: Record<string, unknown> = {};
      const kfzAnswers = pickByKind("kfz");
      const immoAnswers = pickByKind("immobilie");
      const kennzAnswers = pickByKind("kennzahlen");
      if (kfzAnswers) details.kfz = kfzAnswers;
      if (immoAnswers) details.immobilie = immoAnswers;
      if (kennzAnswers) details.kennzahlen = kennzAnswers;

      const res = await fetch(apiUrl(`public-form/${token}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          details,
          progress: statesByKey,
          version: "risikoanalyse-detail-v1",
        }),
      });
      if (res.ok) {
        try {
          window.localStorage.removeItem(DRAFT_LS_PREFIX + token);
        } catch {
          /* ignore */
        }
        setSubmitOk(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>Formular wird geladen…</div>
        </div>
      </div>
    );
  }
  if (!valid) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>Der Link ist ungültig oder abgelaufen.</div>
        </div>
      </div>
    );
  }
  if (submitOk) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <h2 className={styles.pageStateTitle}>Vielen Dank</h2>
          <p className={styles.pageStateBody}>
            Ihre Detail-Erfassung wurde übermittelt. Wir melden uns mit den nächsten Schritten.
          </p>
        </div>
      </div>
    );
  }

  if (activeKey) {
    const state = statesByKey[activeKey];
    return (
      <div className={styles.shell}>
        <TopBar customerName={companyName} onMenuClick={() => {}} />
        <div className={styles.inner}>
          <button type="button" className={styles.backLink} onClick={() => setActiveKey(null)}>
            ← Zurück zur Übersicht
          </button>
          <div className={styles.detailWrap}>
            {state.kind === "kfz" && (
              <RisikoKfzStepForm
                chrome="embedded"
                // Diese Seite ist eine öffentliche Token-Strecke und hat heute
                // KEIN Inhaltsverzeichnis. Ohne diese Zeile fiele der gemessene
                // Container in „menu" und dort erschiene plötzlich ein Knopf,
                // den es hier nie gab.
                toc="none"
                embeddedSubmitLabel="KFZ-Erfassung abschließen"
                onSubmitEmbedded={(answers) => {
                  setDone(activeKey, answers);
                }}
              />
            )}
            {state.kind === "immobilie" && (
              <RisikoImmobilieStepForm
                chrome="embedded"
                // Diese Seite ist eine öffentliche Token-Strecke und hat heute
                // KEIN Inhaltsverzeichnis. Ohne diese Zeile fiele der gemessene
                // Container in „menu" und dort erschiene plötzlich ein Knopf,
                // den es hier nie gab.
                toc="none"
                // Und sie ist eine gewöhnliche Dokumentseite: die Maske steckt
                // hier in einer Karte, nicht in einem Rahmen mit definiter
                // Höhe. Ohne diese Zeile würde die Maske versuchen, in sich zu
                // scrollen — mit dem Ergebnis, dass die Schritt-Leiste ihren
                // Scrollport verlöre und beim Scrollen davonliefe.
                hoehe="inhalt"
                embeddedSubmitLabel="Immobilien-Erfassung abschließen"
                onSubmitEmbedded={async ({ global, gebaeude }) => {
                  setDone(activeKey, { global, gebaeude });
                }}
              />
            )}
            {state.kind === "kennzahlen" && (
              <RisikoKennzahlenStepForm
                chrome="embedded"
                // Siehe oben: hier gab es nie ein Inhaltsverzeichnis.
                toc="none"
                embeddedSubmitLabel="Kennzahlen-Erfassung abschließen"
                onSubmitEmbedded={async (answers) => {
                  setDone(activeKey, answers);
                }}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <TopBar customerName={companyName} onMenuClick={() => {}} />
      <div className={styles.inner}>
        <h1 className={styles.hero}>Detail-Erfassung Ihrer Risiken</h1>
        <p className={styles.description}>
          Auf Basis Ihrer Risikoanalyse haben wir folgende Detail-Bereiche identifiziert. Bitte
          erfassen Sie die nötigen Eckdaten je Bereich. Sie können jederzeit pausieren — Ihr
          Fortschritt wird automatisch gesichert.
        </p>
        {([1, 2, 3, 4] as const).map((b) => {
          const items = grouped[b];
          if (!items || items.length === 0) return null;
          const accent = sectionColorHex[
            b === 1 ? "pillarOrange" : b === 2 ? "blue" : b === 3 ? "green" : "black"
          ];
          return (
            <section key={b} className={styles.bereich}>
              <header className={styles.bereichHead} style={{ backgroundColor: accent }}>
                <span className={styles.bereichBadge}>{b}</span>
                <span>{BEREICH_TITLES[b]}</span>
              </header>
              <div className={styles.cards}>
                {items.map((item) => {
                  const state = statesByKey[item.key] ?? {
                    kind: detailKindFor(item.key),
                    status: "not_started" as const,
                  };
                  const fillable =
                    state.kind === "kfz" ||
                    state.kind === "immobilie" ||
                    state.kind === "kennzahlen";
                  const statusClass =
                    state.status === "done"
                      ? styles.statusDone
                      : state.status === "in_progress"
                      ? styles.statusInProgress
                      : styles.statusNotStarted;
                  const statusLabel =
                    state.status === "done"
                      ? "Abgeschlossen"
                      : state.status === "in_progress"
                      ? "In Bearbeitung"
                      : "Nicht begonnen";
                  return (
                    <div key={item.key} className={styles.card}>
                      <div className={styles.cardHead}>
                        <span className={styles.cardBadge} style={{ backgroundColor: accent }}>
                          {b}
                        </span>
                        <span className={styles.cardTitle}>{item.title}</span>
                        <span className={mergeClasses(styles.statusPill, statusClass)}>{statusLabel}</span>
                      </div>
                      <div className={styles.progressBar}>
                        <div
                          className={styles.progressFill}
                          style={{ width: `${Math.round(computeProgress(state) * 100)}%` }}
                        />
                      </div>
                      <div className={styles.cardBody}>
                        {fillable
                          ? "Bitte erfassen Sie die nötigen Detaildaten."
                          : "Diese Analyse erfassen wir gemeinsam in einem Termin — keine Eingabe nötig."}
                      </div>
                      <div className={styles.cardActions}>
                        {fillable ? (
                          <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={() => {
                              setStatesByKey((prev) => ({
                                ...prev,
                                [item.key]: {
                                  ...(prev[item.key] ?? { kind: state.kind, status: "not_started" }),
                                  status: prev[item.key]?.status === "done" ? "done" : "in_progress",
                                },
                              }));
                              setActiveKey(item.key);
                            }}
                          >
                            {state.status === "done"
                              ? "Bearbeiten"
                              : state.status === "in_progress"
                              ? "Weiter ausfüllen"
                              : "Erfassen"}
                          </button>
                        ) : state.status === "done" ? (
                          <span className={mergeClasses(styles.statusPill, styles.statusDone)}>
                            ✓ Markiert als gelesen
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={styles.doneBtn}
                            onClick={() => setDone(item.key, null)}
                          >
                            Als gelesen markieren
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
        <div className={styles.finalBar}>
          <div className={styles.finalLeft}>
            <h3 className={styles.finalTitle}>Abschluss</h3>
            <span className={styles.finalSub}>
              {allDone
                ? "Alle Detail-Bereiche abgeschlossen — Sie können jetzt übermitteln."
                : "Bitte schließen Sie alle Detail-Bereiche ab, bevor Sie übermitteln."}
            </span>
          </div>
          <button
            type="button"
            className={styles.submitBtn}
            disabled={!allDone || submitting}
            onClick={handleSubmitAll}
          >
            {submitting ? "Wird übermittelt…" : "Alles übermitteln"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RisikoanalyseDetailFormPublic;
